import { ActionContext, ActionTree, GetterTree, MutationTree } from 'vuex'
import { RootState } from '~/store'
import { criticalIcons } from '~/icons/critical'

export enum IconsMutationTypes {
  SET_ICON = 'setIcon',
  SET_LOADING = 'setLoading',
  SET_ERROR = 'setError'
}

export enum IconsActionTypes {
  FETCH_ICON = 'fetchIcon',
  FETCH_ICONS = 'fetchIcons'
}

export enum IconsGetterTypes {
  GET_ICON = 'getIcon',
  IS_LOADING = 'isLoading',
  HAS_ERROR = 'hasError'
}

export interface IconsModuleState {
  icons: Record<string, string>
  loading: Set<string>
  errors: Set<string>
}

export type IconsActionContext = ActionContext<IconsModuleState, RootState>

const pendingRequests = new Map<string, Promise<string | null>>()

export const state = (): IconsModuleState => ({
  icons: { ...criticalIcons },
  loading: new Set(),
  errors: new Set()
})

export const getters: GetterTree<IconsModuleState, RootState> = {
  [IconsGetterTypes.GET_ICON]:
    (state) =>
    (name: string): string | null => {
      return state.icons[name] || null
    },

  [IconsGetterTypes.IS_LOADING]:
    (state) =>
    (name: string): boolean => {
      return state.loading.has(name)
    },

  [IconsGetterTypes.HAS_ERROR]:
    (state) =>
    (name: string): boolean => {
      return state.errors.has(name)
    }
}

export const mutations: MutationTree<IconsModuleState> = {
  [IconsMutationTypes.SET_ICON](state, { name, content }: { name: string; content: string }) {
    state.icons = { ...state.icons, [name]: content }
    state.loading = new Set([...state.loading].filter((n) => n !== name))
    state.errors = new Set([...state.errors].filter((n) => n !== name))
  },

  [IconsMutationTypes.SET_LOADING](state, { name, loading }: { name: string; loading: boolean }) {
    if (loading) {
      state.loading = new Set([...state.loading, name])
    } else {
      state.loading = new Set([...state.loading].filter((n) => n !== name))
    }
  },

  [IconsMutationTypes.SET_ERROR](state, { name }: { name: string }) {
    state.errors = new Set([...state.errors, name])
    state.loading = new Set([...state.loading].filter((n) => n !== name))
  }
}

export interface FetchIconPayload {
  name: string
  baseUrl?: string
}

export const actions: ActionTree<IconsModuleState, RootState> = {
  async [IconsActionTypes.FETCH_ICON](
    { state, commit },
    payload: FetchIconPayload | string
  ): Promise<string | null> {
    // Support both string (legacy) and object payload
    const { name, baseUrl = '' } = typeof payload === 'string' ? { name: payload } : payload

    if (!name) return null

    if (state.icons[name]) {
      return state.icons[name]
    }

    if (pendingRequests.has(name)) {
      return pendingRequests.get(name) as Promise<string | null>
    }

    commit(IconsMutationTypes.SET_LOADING, { name, loading: true })

    const fetchPromise = (async (): Promise<string | null> => {
      try {
        const response = await fetch(`${baseUrl}/server/icons/${name}`)

        if (!response.ok) {
          commit(IconsMutationTypes.SET_ERROR, { name })
          return null
        }

        const data = await response.json()
        commit(IconsMutationTypes.SET_ICON, { name, content: data.content })
        return data.content
      } catch {
        commit(IconsMutationTypes.SET_ERROR, { name })
        return null
      } finally {
        pendingRequests.delete(name)
      }
    })()

    pendingRequests.set(name, fetchPromise)
    return fetchPromise
  },

  async [IconsActionTypes.FETCH_ICONS]({ dispatch }, names: string[]): Promise<void> {
    await Promise.all(names.map((name) => dispatch(IconsActionTypes.FETCH_ICON, name)))
  }
}
